/**
 * Notification Permission Prompt
 * Asks users to enable notifications for calls and messages.
 *
 * HARD ROUTE GATE
 * ───────────────
 * This component is rendered globally in `App.js` so it sits OUTSIDE
 * the protected-route tree. Without a gate it would pop on every
 * public/external page — login, business card view, signup, app-store
 * redirect, etc. — which leaks "Enable Notifications" UI to people
 * who are not even logged in and (worse) on the production iOS
 * Universal-Link target, scaring off external contacts.
 *
 * Allow-list, not deny-list: the only place this prompt is allowed to
 * render is the authenticated Home page (`/`). Every other path — and
 * every unauthenticated session — gets a hard `return null`. Extend
 * the allow-list explicitly if a new authenticated landing surface
 * needs it. This is intentionally restrictive.
 */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';

// Pathnames that may show the prompt. Authenticated-only. Keep tight.
const ALLOWED_PATHS = new Set(['/']);

const NotificationPermissionPrompt = () => {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const onAllowedPage = ALLOWED_PATHS.has(pathname);
  const allowed = isAuthenticated && onAllowedPage;

  const { isSupported, permission, requestPermission, isPWA, isIOS } = useNotifications();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Detect Android (uses its own native browser/PWA prompt — we
  // defer to the OS just like iOS). Anything else (desktop, Windows,
  // misc) gets our friendly in-app prompt.
  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '');
  const skipCustom = isIOS || isAndroid;

  useEffect(() => {
    if (!allowed) { setShow(false); return; }
    // iOS/Android: skip our custom prompt entirely — the OS shows its
    // own permission dialog when `Notification.requestPermission()`
    // fires from elsewhere (Layout.js post-auth hook). Showing two
    // stacked prompts confused users.
    if (skipCustom) { setShow(false); return; }
    const timer = setTimeout(() => {
      const wasDismissed = localStorage.getItem('notification_prompt_dismissed');
      if (isSupported && permission === 'default' && !wasDismissed) {
        setShow(true);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [allowed, isSupported, permission, skipCustom]);

  if (!allowed || skipCustom) return null;

  const handleEnable = async () => {
    const result = await requestPermission();
    if (result === 'granted' || result === 'denied') {
      setShow(false);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    localStorage.setItem('notification_prompt_dismissed', 'true');
  };

  if (!show || dismissed) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom duration-500">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-blue-600" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-sm mb-1">Enable Notifications</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Get notified about incoming calls, messages, and important updates
              {isIOS && !isPWA && " - works best when added to Home Screen"}
            </p>
            
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleEnable}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Enable
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
              >
                Not now
              </Button>
            </div>
          </div>
          
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 -mr-2 -mt-1"
            onClick={handleDismiss}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionPrompt;
